import * as Yup from 'yup'
import React from 'react'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-hot-toast'
import { useParams } from 'react-router-dom'

import { CustomerUser, CustomerUsersError, CustomerUserTDO } from '../../types/customer-user'
import placeService from '../../services/placeService'
import customerUserService from '../../services/customerUsersService'
import { customerUserRoles } from '../../utils/config'
import { queryClient } from '../../utils/react-query'

import Drawer from '../Drawer'
import Spinner from '../Spinner'
import ErrorPage from '../ErrorPage'
import DeleteButton from '../DeleteButton'
import UserDrawerForm from './UserDrawerForm'
import { BuzzlUsersError } from '../../types/buzzl-user'

type UserDrawerProps = {
  onClose: () => void
  open: boolean
  selectedUser?: CustomerUser
  defaultCustomerId?: string
}

const schema = Yup.object().shape({
  role: Yup.string()
    .oneOf([
      customerUserRoles.admin,
      customerUserRoles.manager,
      customerUserRoles.operator,
      customerUserRoles.superAdmin,
    ])
    .required(),
  displayName: Yup.string().required().label('Name'),
  email: Yup.string().email().required(),
  places: Yup.array().of(Yup.string()),
})

const UserDrawer: React.FC<UserDrawerProps> = ({ onClose, open, selectedUser, defaultCustomerId }) => {
  const { customerId = defaultCustomerId } = useParams<{ customerId: string }>()

  const {
    data: places,
    isLoading,
    isError,
  } = useQuery(['places', customerId], () => placeService.getAll({ where: { customer: { id: customerId } } }), {
    select: (data) => {
      return data.map(({ name, id }) => ({ label: name, value: id }))
    },
  })

  const newCustomerUser = useMutation(customerUserService.createCustomerUser, {
    onSuccess: async () => {
      queryClient.refetchQueries('customerUsers')
      toast.success('Account created.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const updateCustomerUser = useMutation(customerUserService.updateCustomerUser, {
    onSuccess: async () => {
      queryClient.refetchQueries('customerUsers')
      toast.success('Account updated.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const deleteCustomerUser = useMutation(customerUserService.deleteCustomerUser, {
    onSuccess: async () => {
      queryClient.refetchQueries('customerUsers')
      toast.success('Account deleted.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const initialValues: CustomerUserTDO = {
    role: selectedUser?.role || customerUserRoles.superAdmin,
    displayName: selectedUser?.user?.displayName || '',
    email: selectedUser?.user?.email || '',
    places:
      selectedUser?.assignedPlaces && selectedUser.assignedPlaces.length > 0
        ? selectedUser.assignedPlaces.map(({ id }) => id)
        : [],
  }

  const handleOnSubmit = async (values: CustomerUserTDO, formikBag: FormikHelpers<CustomerUserTDO>) => {
    try {
      if (selectedUser) {
        //known bug: don't send the same username to the backend, because it doesn't let you save the user
        const { displayName, ...rest } = values
        await updateCustomerUser.mutateAsync({
          id: selectedUser.id,
          ...rest,
          ...(displayName !== selectedUser?.user?.displayName && {
            displayName: displayName,
          }),
        })
      } else {
        await newCustomerUser.mutateAsync({ ...values, customerId })
      }
      onClose()
    } catch (e) {
      const error = e as CustomerUsersError

      error.response.errors.forEach((err) => {
        if (err.extensions.code === 'customer-user/email-already-exists') {
          formikBag.setFieldError('email', err.message)
        }
        if (err.extensions.code === 'end-user/no-valid-unique-user-name') {
          formikBag.setFieldError('displayName', err.message)
        }
        //update user endpoint gives back 500 error if you send the same displayName
        if (err.extensions.code === 'user/no-valid-unique-user-name') {
          formikBag.setFieldError('displayName', err.message)
        }
      })
    }
  }

  if (isLoading) return null

  return (
    <Drawer open={open} onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleOnSubmit}
      >
        {({ isSubmitting, dirty, values }: FormikProps<CustomerUserTDO>) => (
          <Drawer.FormContainer
            open={open}
            onClose={onClose}
            title="Add User"
            Footer={
              !isError ? (
                <>
                  <button className="btn btn-white" onClick={onClose} disabled={isSubmitting} type="button">
                    Cancel
                  </button>

                  {selectedUser && (
                    <DeleteButton
                      type="button"
                      className="btn btn-delete ml-4"
                      modalTitle="Delete account"
                      modalText="Once you delete your account, you will lose all data associated with
                    it. This action is irreversible. Please continue
                    carefully."
                      disabled={deleteCustomerUser.isLoading}
                      onClick={async () => {
                        await deleteCustomerUser.mutateAsync(selectedUser.id)
                        onClose()
                      }}
                    >
                      {deleteCustomerUser.isLoading ? (
                        <div className="mr-3">
                          <Spinner className="trailing-icon h-4 w-4" />
                        </div>
                      ) : null}
                      Delete account
                    </DeleteButton>
                  )}

                  <button className="btn btn-primary ml-4" type="submit" disabled={isSubmitting || !dirty}>
                    {isSubmitting ? (
                      <>
                        <Spinner className="h-5 w-5 text-gray-700" aria-hidden="true" />
                        Loading
                      </>
                    ) : selectedUser ? (
                      'Update'
                    ) : (
                      'Create'
                    )}
                  </button>
                </>
              ) : (
                <></>
              )
            }
          >
            {isError || !places ? (
              <ErrorPage
                title="Something went wrong!"
                text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
              />
            ) : (
              <UserDrawerForm
                isSubmitting={isSubmitting}
                places={places}
                showCustomers={values.role === customerUserRoles.operator || values.role === customerUserRoles.manager}
              />
            )}
          </Drawer.FormContainer>
        )}
      </Formik>
    </Drawer>
  )
}

export default UserDrawer
